import api from '@/lib/api';
import { narrationSegmentToScene } from '@/lib/sceneGridNarrationAdapter';
import { sanitizeEditorSnapshotForStorage } from '@/lib/mediaUrls';
import {
  NARRATION_EDITOR_TIMELINE_LANES,
  editorSnapshotTracksFromLanes,
} from '@/lib/narrationEditorLoad';

export { NARRATION_EDITOR_TIMELINE_LANES, editorSnapshotTracksFromLanes };

/** Push local timeline edits to the server on this interval (ms). */
export const CLOUD_EDITOR_AUTOSAVE_INTERVAL_MS = 5 * 1000;

/** @deprecated Version history removed — kept for legacy imports. */
export const CLOUD_EDITOR_VERSION_INTERVAL_MS = CLOUD_EDITOR_AUTOSAVE_INTERVAL_MS;

/** Default page size for cloud project version history lists. */
export const CLOUD_VIDEO_EDITOR_SNAPSHOT_PAGE_SIZE = 20;

export async function fetchNarrationEditorShell(projectId) {
  const { data } = await api.get(`/narration/projects/${projectId}/editor-shell`, {
    params: { _editorFresh: Date.now() },
  });
  return data;
}

export async function fetchNarrationEditorScenes(projectId) {
  const { data } = await api.get(`/narration/projects/${projectId}/editor-scenes`, {
    params: { _editorFresh: Date.now() },
  });
  return data;
}

export async function fetchNarrationEditorTimelineLane(projectId, lane) {
  const { data } = await api.get(
    `/narration/projects/${projectId}/editor-timelines/${lane}`,
    { params: { _editorFresh: Date.now() } },
  );
  return data;
}

/** Fetch every timeline lane in parallel. `onLane` runs as each response arrives. */
export async function fetchNarrationEditorTimelines(projectId, onLane) {
  const rows = await Promise.all(
    NARRATION_EDITOR_TIMELINE_LANES.map(async ({ lane, field }) => {
      try {
        const data = await fetchNarrationEditorTimelineLane(projectId, lane);
        if (typeof onLane === 'function') onLane(data);
        return data;
      } catch (err) {
        // One bad lane must not blank the whole editor (large projects / flaky media resolve).
        console.warn(`editor timeline lane failed: ${lane}`, err);
        const empty = { lane, field: field || `${lane}Tracks`, tracks: [[]] };
        if (typeof onLane === 'function') onLane(empty);
        return empty;
      }
    }),
  );
  return editorSnapshotTracksFromLanes(rows);
}

/**
 * Paginated cloud snapshot list for project version history.
 * @returns {Promise<{ entries: { id: string, saved_at?: string }[], total: number, limit: number, offset: number, has_more: boolean }>}
 */
export async function fetchCloudVideoEditorSnapshots({
  isNarration = false,
  projectId = null,
  storyId = null,
  limit = CLOUD_VIDEO_EDITOR_SNAPSHOT_PAGE_SIZE,
  offset = 0,
  aroundSnapshotId = null,
} = {}) {
  const params = { limit, offset };
  if (aroundSnapshotId) params.around_snapshot_id = aroundSnapshotId;
  if (isNarration && projectId === 'demo-preview') return { entries: [], total: 0, limit, offset, has_more: false };
  if (isNarration && projectId) {
    const { data } = await api.get(`/narration/projects/${projectId}/video-editor-snapshots`, { params });
    return data;
  }
  if (!isNarration && storyId) {
    const { data } = await api.get(`/stories/${storyId}/video-editor-snapshots`, { params });
    return data;
  }
  return { entries: [], total: 0, limit, offset, has_more: false };
}

/**
 * Build PATCH body: `{ editor, scenes?, story? }` (legacy flat editor still accepted server-side).
 */
export function cloudSnapshotPayloadFromDoc(doc) {
  if (!doc?.editor || typeof doc.editor !== 'object') return null;
  const payload = {
    editor: sanitizeEditorSnapshotForStorage(doc.editor),
  };
  if (Array.isArray(doc.scenes) && doc.scenes.length) {
    payload.scenes = JSON.parse(JSON.stringify(doc.scenes));
  }
  if (doc.story && typeof doc.story === 'object') {
    payload.story = doc.story;
  }
  return payload;
}

/**
 * Persist video editor timeline + optional scene/story snapshot to MongoDB.
 * @param {{
 *   isNarration: boolean,
 *   projectId?: string,
 *   storyId?: string,
 *   editor: object,
 *   scenes?: object[],
 *   story?: object,
 *   recordVersion?: boolean,
 * }} opts
 */
export async function fetchVideoEditorSnapshotFromCloud({
  isNarration = false,
  projectId = null,
  storyId = null,
} = {}) {
  const fresh = { params: { _editorFresh: Date.now() } };
  if (isNarration && projectId === 'demo-preview') return null;
  if (isNarration && projectId) {
    const { data } = await api.get(`/narration/projects/${projectId}`, fresh);
    return data?.video_editor_snapshot && typeof data.video_editor_snapshot === 'object'
      ? data.video_editor_snapshot
      : null;
  }
  if (!isNarration && storyId) {
    const { data } = await api.get(`/stories/${storyId}`, fresh);
    return data?.video_editor_snapshot && typeof data.video_editor_snapshot === 'object'
      ? data.video_editor_snapshot
      : null;
  }
  return null;
}

export async function patchVideoEditorSnapshotToCloud({
  isNarration,
  projectId,
  storyId,
  editor,
  scenes,
  story,
  recordVersion = false,
  basedOnSnapshotId = null,
}) {
  if (!editor || typeof editor !== 'object') {
    throw new Error('Missing editor snapshot');
  }
  if (isNarration && projectId === 'demo-preview') return null;
  const body = {
    editor,
    ...(Array.isArray(scenes) ? { scenes } : {}),
    ...(story && typeof story === 'object' ? { story } : {}),
    ...(basedOnSnapshotId ? { based_on_snapshot_id: basedOnSnapshotId } : {}),
  };
  const params = recordVersion ? {} : { record_version: false };
  if (isNarration && projectId) {
    const { data } = await api.patch(
      `/narration/projects/${projectId}/video-editor-snapshot`,
      body,
      { params },
    );
    return data;
  }
  if (!isNarration && storyId) {
    const { data } = await api.patch(`/stories/${storyId}/video-editor-snapshot`, body, { params });
    return data;
  }
  throw new Error('No project or story id for cloud sync');
}

/** Persist or clear uncommitted whole-script language preview on the narration project. */
export async function saveScriptLanguageDraftToCloud(projectId, draft) {
  if (!projectId || projectId === 'demo-preview') return;
  if (!draft?.language) {
    await api.delete(`/narration/projects/${projectId}/script-language-draft`);
    return;
  }
  await api.put(`/narration/projects/${projectId}/script-language-draft`, {
    language: draft.language,
    segments: (draft.segments || []).map((s) => ({
      id: s.id,
      text: s.text ?? '',
      visual: s.visual ?? '',
      phoneme_notes: s.phoneme_notes ?? '',
    })),
    regenerate_audio: Boolean(draft.regenerate_audio),
  });
}

/** Append editor state as a new cloud version (narration always versions; story uses interval). */
export async function appendVideoEditorCloudVersion({
  isNarration = false,
  projectId = null,
  storyId = null,
  doc,
  basedOnSnapshotId = null,
} = {}) {
  const payload = cloudSnapshotPayloadFromDoc(doc);
  if (!payload) throw new Error('Missing editor document');
  return patchVideoEditorSnapshotToCloud({
    isNarration,
    projectId,
    storyId,
    editor: payload.editor,
    scenes: payload.scenes,
    story: payload.story,
    recordVersion: false,
    basedOnSnapshotId,
  });
}

async function loadScenesWithUpdatedSegments(projectId, segList) {
  const { data: projectData } = await api.get(`/narration/projects/${projectId}`, {
    params: { _editorFresh: Date.now() },
  });
  if (!projectData?.id) return null;

  let scenes = [];
  if (Array.isArray(projectData.video_editor_scenes_snapshot) && projectData.video_editor_scenes_snapshot.length) {
    scenes = JSON.parse(JSON.stringify(projectData.video_editor_scenes_snapshot));
  } else if (Array.isArray(projectData.segments)) {
    scenes = projectData.segments.map((seg) => narrationSegmentToScene(seg, {
      id: projectId,
      title: projectData.title,
      app: projectData.app,
      input: projectData.input || {},
    }));
  }
  if (!scenes.length) return null;

  for (const segment of segList) {
    const sceneDoc = narrationSegmentToScene(segment, {
      id: projectId,
      title: projectData.title,
      app: projectData.app,
      input: projectData.input || {},
    });
    const idx = scenes.findIndex((s) => String(s.id) === String(segment.id));
    if (idx >= 0) {
      scenes[idx] = { ...scenes[idx], ...sceneDoc };
    } else {
      scenes.push(sceneDoc);
    }
  }

  return { projectId, scenes };
}

/**
 * After narration audio regen on Scenes/Edit, append a new cloud editor version:
 * frozen scenes + synced timeline audio lanes, then record as current head version.
 * @param {string} projectId
 * @param {object|object[]} segment — one or more updated narration segments
 */
export async function appendCloudVersionAfterSegmentAudioChange(
  projectId,
  segment,
) {
  if (!projectId) return null;
  if (projectId === 'demo-preview') return null;
  const segList = (Array.isArray(segment) ? segment : [segment]).filter((s) => s?.id);
  if (!segList.length) return null;

  const merged = await loadScenesWithUpdatedSegments(projectId, segList);
  if (!merged?.scenes?.length) return null;

  // Align head editor audio/video lanes with live narration_segments (bootstraps if missing).
  await api.post(`/narration/projects/${merged.projectId}/sync-video-editor-from-segments`);

  const { data: project } = await api.get(`/narration/projects/${merged.projectId}`, {
    params: { _editorFresh: Date.now() },
  });
  const editor = project?.video_editor_snapshot;
  if (!editor || typeof editor !== 'object') return null;

  return patchVideoEditorSnapshotToCloud({
    isNarration: true,
    projectId: merged.projectId,
    editor,
    scenes: merged.scenes,
    recordVersion: false,
  });
}

export function scriptLanguageDraftFromProject(project) {
  const d = project?.script_language_draft;
  if (!d || typeof d !== 'object') return null;
  const language = String(d.language || '').trim();
  if (!language) return null;
  if (!Array.isArray(d.segments) || !d.segments.length) return null;
  return {
    language,
    segments: d.segments,
    regenerate_audio: Boolean(d.regenerate_audio),
  };
}

/** Rebuild cloud video-editor snapshot from narration_segments (after /edit saves). */
export async function syncNarrationVideoEditorFromSegments(projectId) {
  if (!projectId || projectId === 'demo-preview') return null;
  const { data } = await api.post(
    `/narration/projects/${projectId}/sync-video-editor-from-segments`,
  );
  return data;
}

export function cloudEditorSnapshotIsNewer(cloudSavedAt, localSavedAt) {
  const tCloud = Date.parse(cloudSavedAt || '') || 0;
  const tLocal = Date.parse(localSavedAt || '') || 0;
  if (!tCloud) return false;
  if (!tLocal) return true;
  return tCloud >= tLocal;
}
